import { Icons } from '@/assets/icons/icons';
import { type ModalFC } from '@/components/modals/modalsManager';
import { Dialog } from '@/components/ui/dialog';
import { XStack, YStack } from '@/components/ui/layout/layout';
import { Separator } from '@/components/ui/Separator';
import { isMac } from '@/utils/capabilities';

export type VsCodeForkApp = 'cursor' | 'vscode' | 'windsurf' | 'antigravity';

interface VsCodeForkAccessibilityModalProps {
  app: VsCodeForkApp;
}

export const VsCodeForkAccessibilityModal: ModalFC<
  VsCodeForkAccessibilityModalProps
> = ({ open, onOpenChange, app }) => {
  const commandPaletteShortcut = isMac() ? 'Cmd+Shift+P' : 'Ctrl+Shift+P';

  let appName: string;
  switch (app) {
    case 'cursor':
      appName = 'Cursor';
      break;
    case 'vscode':
      appName = 'VS Code';
      break;
    case 'windsurf':
      appName = 'Windsurf';
      break;
    case 'antigravity':
      appName = 'Antigravity';
      break;
  }

  let menuBarLabel: string;
  switch (app) {
    case 'cursor':
      menuBarLabel = 'Cursor';
      break;
    case 'vscode':
      menuBarLabel = 'Code';
      break;
    case 'windsurf':
      menuBarLabel = 'Windsurf';
      break;
    case 'antigravity':
      menuBarLabel = 'Antigravity';
      break;
  }

  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Content className="max-w-xl w-full bg-background overflow-y-auto scrollbar-hide items-start content-start p-8">
        <Dialog.Header className="space-y-2">
          <Dialog.Title className="text-xl">
            Enable {appName} Screen Reader Mode
          </Dialog.Title>
          <Dialog.Description className="text-sm leading-relaxed">
            Littlebird needs Editor: Accessibility Support enabled to read your
            open files in {appName}.
          </Dialog.Description>
        </Dialog.Header>

        <YStack className="gap-6 w-full mt-2">
          <YStack className="gap-4">
            <span className="text-sm font-medium">Use the command palette</span>
            <YStack className="gap-3">
              <MenuStep number={1} text="Press" bold={commandPaletteShortcut} />
              <MenuStep
                number={2}
                text="Type"
                bold="Toggle Screen Reader Accessibility Mode"
              />
              <MenuStep number={3} text="Press" bold="Enter" />
            </YStack>
          </YStack>

          <XStack className="items-center gap-3 w-full">
            <Separator className="flex-1" />
            <span className="text-xs font-medium text-foreground-muted uppercase tracking-wider">
              or
            </span>
            <Separator className="flex-1" />
          </XStack>

          <YStack className="gap-4">
            <span className="text-sm font-medium">Use Settings</span>
            <YStack className="gap-3">
              <MenuStep
                number={1}
                text="From the menu bar, click"
                bold={menuBarLabel}
              />
              <SettingsMenuSteps app={app} />
            </YStack>
          </YStack>

          <Separator />

          <YStack className="gap-3">
            <span className="text-sm font-medium">How to know it worked</span>
            <XStack className="items-center gap-3 rounded-lg border border-border-strong bg-surface/50 p-4">
              <Icons.circleCheck className="size-5 text-foreground-muted shrink-0" />
              <p className="text-sm text-foreground-muted leading-relaxed">
                A{' '}
                <strong className="text-foreground font-medium">
                  Screen Reader Optimized
                </strong>{' '}
                pill will appear in the {appName} status bar.
              </p>
            </XStack>
          </YStack>

          <p className="text-xs text-foreground-muted leading-relaxed">
            This setting is saved by {appName} per-machine.
          </p>
        </YStack>
      </Dialog.Content>
    </Dialog.Root>
  );
};

function SettingsMenuSteps({ app }: { app: VsCodeForkApp }) {
  switch (app) {
    case 'cursor':
      return (
        <>
          <MenuStep number={2} text="Select" bold="Cursor Settings…" />
          <MenuStep
            number={3}
            text="In the sidebar, click"
            bold="VS Code Settings"
          />
          <MenuStep
            number={4}
            text="Search for"
            bold="Editor: Accessibility Support"
          />
          <MenuStep number={5} text="Set it to" bold="On" />
        </>
      );
    case 'vscode':
      return (
        <>
          <MenuStep number={2} text="Select" bold="Settings → Settings" />
          <MenuStep
            number={3}
            text="Search for"
            bold="Editor: Accessibility Support"
          />
          <MenuStep number={4} text="Set it to" bold="On" />
        </>
      );
    case 'windsurf':
    case 'antigravity':
      return (
        <>
          <MenuStep number={2} text="Hover" bold="Settings…" />
          <MenuStep number={3} text="Click" bold="Editor Settings" />
          <MenuStep
            number={4}
            text="Search for"
            bold="Editor: Accessibility Support"
          />
          <MenuStep number={5} text="Set it to" bold="On" />
        </>
      );
  }
}

function MenuStep({
  number,
  text,
  bold,
}: {
  number: number;
  text: string;
  bold: string;
}) {
  return (
    <XStack className="items-baseline gap-2.5 text-sm text-foreground-muted">
      <span className="text-xs font-medium text-foreground-muted/60 w-3 shrink-0">
        {number}.
      </span>
      <span>
        {text} <strong className="text-foreground font-medium">{bold}</strong>
      </span>
    </XStack>
  );
}
